import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Map as MapIcon, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph } from "../lib/schema";
import { LANDING_PAGES } from "../data/landingPages";

// Human-readable sitemap page.  Complements /api/sitemap.xml (for crawlers)
// by giving patients and Google a single categorised index of EVERY
// indexable URL on the site.
//
// Data sources:
//   • Static routes hard-coded below (main pages + hub pages)
//   • LANDING_PAGES  → 9 city+service landing pages
//   • /api/locations → 23 district + neighborhood pages
//   • /api/expertise → 10 clinical expertise pages
//   • /api/blog      → all CMS blog posts (30+)
//   • /api/hub       → all content-hub articles
//   • /api/guides    → downloadable patient guides

const MAIN_PAGES = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About Dr. Shantanu Kumar Gupta" },
  { path: "/team/dr-manisha-mishra", label: "About Dr. Manisha Mishra" },
  { path: "/expertise", label: "Areas of Expertise" },
  { path: "/content-hub", label: "Content Hub" },
  { path: "/blog", label: "Blog & Insights" },
  { path: "/guides", label: "Patient Guides (PDF)" },
  { path: "/publications", label: "Peer-Reviewed Publications" },
  { path: "/press", label: "Press Kit & Media Coverage" },
  { path: "/videos", label: "Videos, Shorts & Reels" },
  { path: "/eye-cases", label: "Eye Case Studies (Clinical Imaging Atlas)" },
  { path: "/iol-selector", label: "IOL Lens Selector Tool — Interactive Assessment" },
  { path: "/testimonials", label: "Patient Testimonials" },
  { path: "/media", label: "Media Appearances" },
  { path: "/faq", label: "Frequently Asked Questions" },
  { path: "/insurance", label: "Insurance & Cashless" },
  { path: "/appointment", label: "Book an Appointment" },
  { path: "/contact", label: "Contact & Directions" },
];

const HUB_PAGES = [
  { path: "/best-eye-doctor-varanasi", label: "Best Eye Doctor in Varanasi (Hub)" },
  { path: "/cataract-surgery-cost-in-varanasi", label: "Cataract Surgery Cost in Varanasi" },
  { path: "/tools/cataract-cost-calculator", label: "Cataract Surgery Cost Calculator (Tool)" },
  { path: "/emergency-eye-care-varanasi", label: "Emergency Eye Care in Varanasi (WhatsApp Triage)" },
  { path: "/best-cataract-surgeon-in-varanasi", label: "Best Cataract Surgeon in Varanasi" },
  { path: "/best-retina-specialist-in-varanasi", label: "Best Retina Specialist in Varanasi" },
  { path: "/best-glaucoma-specialist-in-varanasi", label: "Best Glaucoma Specialist in Varanasi" },
  { path: "/best-pediatric-eye-specialist-in-varanasi", label: "Best Paediatric Eye Specialist in Varanasi" },
  { path: "/best-cornea-specialist-in-varanasi", label: "Best Cornea Specialist in Varanasi" },
  { path: "/best-squint-surgeon-in-varanasi", label: "Best Squint Surgeon in Varanasi" },
  { path: "/best-oculoplasty-surgeon-in-varanasi", label: "Best Oculoplasty Surgeon in Varanasi" },
  { path: "/insurance/ayushman-bharat-eye-hospital-varanasi", label: "Ayushman Bharat Eye Hospital in Varanasi" },
  { path: "/cashless-eye-surgery-varanasi", label: "Cashless Eye Surgery in Varanasi" },
  { path: "/emergency-eye-doctor-varanasi", label: "Emergency Eye Doctor in Varanasi" },
  { path: "/second-opinion-eye-surgery-varanasi", label: "Second Opinion for Eye Surgery in Varanasi" },
  { path: "/review", label: "Leave a Google Review" },
];

const Section = ({ title, count, children, testid }) => (
  <section className="mb-14" data-testid={testid}>
    <div className="flex items-baseline justify-between mb-5 pb-3 border-b border-border">
      <h2 className="font-serif text-2xl sm:text-3xl">{title}</h2>
      {typeof count === "number" && (
        <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          {count} {count === 1 ? "page" : "pages"}
        </span>
      )}
    </div>
    {children}
  </section>
);

const LinkList = ({ items }) => (
  <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2.5">
    {items.map((it) => (
      <li key={it.path} className="group">
        <Link
          to={it.path}
          data-testid={`sitemap-link-${it.path.replace(/\//g, "-").replace(/^-/, "")}`}
          className="text-sm inline-flex items-start gap-2 leading-snug hover:text-[hsl(var(--accent))] transition-colors"
        >
          <ArrowRight
            size={12}
            className="mt-1 shrink-0 text-[hsl(var(--accent))]/60 group-hover:translate-x-0.5 transition-transform"
          />
          <span>{it.label}</span>
        </Link>
      </li>
    ))}
  </ul>
);

const Sitemap = () => {
  usePageMeta({
    title: "Sitemap — All Pages | Dr. Shantanu Kumar Gupta, Varanasi",
    path: "/sitemap.html",
    description:
      "Complete site index — every service, location, blog post, guide and doctor profile on drshantanugupta.com. Human-readable companion to the XML sitemap.",
  });
  usePageJsonLd(
    graph([breadcrumb([{ name: "Home", path: "/" }, { name: "Sitemap", path: "/sitemap.html" }])])
  );

  const [locations, setLocations] = useState([]);
  const [expertise, setExpertise] = useState([]);
  const [blog, setBlog] = useState([]);
  const [hub, setHub] = useState([]);
  const [guides, setGuides] = useState([]);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data || [])).catch(() => {});
    api.get("/expertise").then((r) => setExpertise(r.data || [])).catch(() => {});
    api.get("/blog").then((r) => setBlog(r.data || [])).catch(() => {});
    api.get("/hub").then((r) => setHub(r.data || [])).catch(() => {});
    api.get("/guides").then((r) => setGuides(r.data || [])).catch(() => {});
    api.get("/cases").then((r) => setCases(r.data || [])).catch(() => {});
  }, []);

  const districts = locations.filter((l) => l.page_type !== "neighborhood");
  const neighborhoods = locations.filter((l) => l.page_type === "neighborhood");

  const landingItems = LANDING_PAGES.map((p) => ({ path: `/${p.slug}`, label: p.h1 }));
  const districtItems = districts.map((l) => ({
    path: `/locations/${l.slug}`,
    label: `Eye Doctor for ${l.city} patients`,
  }));
  const neighborhoodItems = neighborhoods.map((l) => ({
    path: `/varanasi/${l.slug}`,
    label: `Eye Doctor near ${l.city}, Varanasi`,
  }));
  const expertiseItems = expertise.map((e) => ({
    path: `/expertise/${e.slug}`,
    label: e.title,
  }));
  const blogItems = blog.map((b) => ({ path: `/blog/${b.slug}`, label: b.title }));
  const hubItems = hub.map((h) => ({ path: `/content-hub/${h.slug}`, label: h.title }));
  const guideItems = guides.map((g) => ({ path: `/guides#${g.slug}`, label: g.title }));
  const caseItems = cases.map((c) => ({ path: `/eye-cases/${c.slug}`, label: c.title }));

  const totalCount =
    MAIN_PAGES.length +
    HUB_PAGES.length +
    landingItems.length +
    districtItems.length +
    neighborhoodItems.length +
    expertiseItems.length +
    blogItems.length +
    hubItems.length +
    guideItems.length +
    caseItems.length;

  return (
    <div data-testid="sitemap-page">
      <section className="pt-20 pb-8 container-page">
        <div className="overline mb-4 inline-flex items-center gap-2">
          <MapIcon size={12} /> Site index
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Sitemap — every page on drshantanugupta.com
        </h1>
        <p className="mt-5 max-w-2xl prose-elegant">
          A single index of every service, location, expertise, blog post and guide on the site — for patients navigating without
          search, and for Google. If you're looking for the machine-readable version, see the{" "}
          <a
            href="/api/sitemap.xml"
            className="underline underline-offset-4 hover:text-[hsl(var(--accent))]"
            data-testid="sitemap-xml-link"
          >
            XML sitemap
          </a>
          .
        </p>
        <div className="mt-6 inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))]" />
          {totalCount} indexed pages
        </div>
      </section>

      <section className="pb-24 container-page">
        <Section title="Main pages" count={MAIN_PAGES.length} testid="sitemap-section-main">
          <LinkList items={MAIN_PAGES} />
        </Section>

        <Section title="Best-of & pricing hubs" count={HUB_PAGES.length} testid="sitemap-section-hubs">
          <LinkList items={HUB_PAGES} />
        </Section>

        <Section
          title="Services in Varanasi"
          count={landingItems.length}
          testid="sitemap-section-landing"
        >
          <LinkList items={landingItems} />
        </Section>

        {expertiseItems.length > 0 && (
          <Section
            title="Clinical expertise"
            count={expertiseItems.length}
            testid="sitemap-section-expertise"
          >
            <LinkList items={expertiseItems} />
          </Section>
        )}

        {districtItems.length > 0 && (
          <Section
            title="Patients from these districts"
            count={districtItems.length}
            testid="sitemap-section-districts"
          >
            <LinkList items={districtItems} />
          </Section>
        )}

        {neighborhoodItems.length > 0 && (
          <Section
            title="Varanasi neighborhoods"
            count={neighborhoodItems.length}
            testid="sitemap-section-neighborhoods"
          >
            <LinkList items={neighborhoodItems} />
          </Section>
        )}

        {blogItems.length > 0 && (
          <Section title="Blog posts" count={blogItems.length} testid="sitemap-section-blog">
            <LinkList items={blogItems} />
          </Section>
        )}

        {hubItems.length > 0 && (
          <Section
            title="Content hub articles"
            count={hubItems.length}
            testid="sitemap-section-hub"
          >
            <LinkList items={hubItems} />
          </Section>
        )}

        {caseItems.length > 0 && (
          <Section
            title="Eye case studies (clinical imaging)"
            count={caseItems.length}
            testid="sitemap-section-cases"
          >
            <LinkList items={caseItems} />
          </Section>
        )}

        {guideItems.length > 0 && (
          <Section
            title="Patient guides (PDF)"
            count={guideItems.length}
            testid="sitemap-section-guides"
          >
            <LinkList items={guideItems} />
          </Section>
        )}
      </section>
    </div>
  );
};

export default Sitemap;
